import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { TempAccessSessionService } from 'app/services/temp-access-session.service';
import { ItemEquivalenceGroup } from '../models/item-equivalence-group.model';

@Injectable({ providedIn: 'root' })
export class ItemEquivalenceService {
  private readonly configBase = environment.apiBaseUrl_prices;
  private _cache: ItemEquivalenceGroup[] = [];
  private readonly _itemsById = new Map<number, any>();

  constructor(private http: HttpClient, private tempAccessSession: TempAccessSessionService) {}

  loadEquivalences(itemIds: number[]): Observable<ItemEquivalenceGroup[]> {
    const normalizedIds = this.normalizeItemIds(itemIds);
    if (!normalizedIds.length) {
      return of([]);
    }

    let params = new HttpParams();
    normalizedIds.forEach(id => (params = params.append('itemIds', String(id))));

    const path = this.tempAccessSession.isValid()
      ? 'item-variants/temporary-access/equivalence-groups'
      : 'item-variants/equivalence-groups';
    const url = `${this.configBase}${path}`;
    return this.http.get<ItemEquivalenceGroup[]>(url, { params }).pipe(
      map(groups => this.normalizeGroups(groups ?? [])),
      tap(groups => this.replaceGroupsForRequestedIds(normalizedIds, groups)),
      catchError(() => of([]))
    );
  }

  ensureEquivalences(itemIds: number[]): Promise<ItemEquivalenceGroup[]> {
    return this.loadEquivalences(itemIds).pipe(first()).toPromise();
  }

  clearCache(): void {
    this._cache = [];
    this._itemsById.clear();
  }

  mergeItemData(items: any[]): void {
    (items || []).forEach(item => {
      const itemId = this.toNumber(item?.itemId);
      if (!(itemId > 0)) return;

      const current = this._itemsById.get(itemId) || {};
      this._itemsById.set(itemId, { ...current, ...item });
    });
  }

  getItemData(itemId: number): any | null {
    const id = this.toNumber(itemId);
    return id != null ? this._itemsById.get(id) ?? null : null;
  }

  getGroupForItem(itemId: number): ItemEquivalenceGroup | null {
    const id = this.toNumber(itemId);
    if (id == null) return null;
    return this._cache.find(g => g.memberItemIds.includes(id)) ?? null;
  }

  getGroupByPrincipal(groupId: number): ItemEquivalenceGroup | null {
    const id = this.toNumber(groupId);
    if (id == null) return null;
    return this._cache.find(g => g.groupId === id) ?? null;
  }

  hasValidGroupForPrincipal(groupId: number): boolean {
    const group = this.getGroupByPrincipal(groupId);
    return !!(group && group.memberItemIds.includes(group.groupId));
  }

  isItemInGroup(itemId: number): boolean {
    return !!this.getGroupForItem(itemId);
  }

  getPrincipalIdForItem(itemId: number): number | null {
    return this.getGroupForItem(itemId)?.groupId ?? null;
  }

  isGroupAlreadyPlaced(groupId: number, planogramProds: any[]): boolean {
    const id = this.toNumber(groupId);
    if (id == null) return false;
    return this.getPlacedGroupIds(planogramProds).has(id);
  }

  getPlacedGroupIds(planogramProds: any[]): Set<number> {
    const placedGroupIds = new Set<number>();
    (planogramProds || []).forEach(prod => {
      if (!prod?.isEquivalenceGroup) return;

      const itemId = this.toNumber(prod.itemData?.itemId ?? prod.itemId);
      const group = this.getGroupForItem(itemId);
      if (group?.groupId != null) placedGroupIds.add(group.groupId);
    });

    return placedGroupIds;
  }

  getGroupMemberItemData(group: ItemEquivalenceGroup): any[] {
    return (group?.memberItemIds || []).map(memberId => this.getItemData(memberId)).filter(Boolean);
  }

  private replaceGroupsForRequestedIds(requestedIds: number[], groups: ItemEquivalenceGroup[]): void {
    const requested = new Set(requestedIds);
    const requestedGroups = new Set(groups.map(group => group.groupId));

    const preservedGroups = this._cache.filter(group => {
      const wasRequested = group.memberItemIds.some(memberId => requested.has(memberId)) || requested.has(group.groupId);
      return !wasRequested || requestedGroups.has(group.groupId);
    });

    const byGroupId = new Map<number, ItemEquivalenceGroup>();
    preservedGroups.forEach(group => byGroupId.set(group.groupId, group));
    groups.forEach(group => byGroupId.set(group.groupId, group));

    this._cache = Array.from(byGroupId.values());
  }

  private normalizeGroups(groups: ItemEquivalenceGroup[]): ItemEquivalenceGroup[] {
    return (groups || [])
      .map(group => {
        const groupId = this.toNumber(group?.groupId);
        if (!(groupId > 0)) return null;

        const memberItemIds = this.normalizeItemIds([groupId, ...(group.memberItemIds || [])]);
        return { groupId, memberItemIds };
      })
      .filter((group): group is ItemEquivalenceGroup => !!group);
  }

  private normalizeItemIds(itemIds: number[]): number[] {
    return Array.from(new Set((itemIds || []).map(id => this.toNumber(id)).filter((id): id is number => id != null && id > 0)));
  }

  private toNumber(value: any): number | null {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }
}
